import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  FormControl,
  Select,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ClearIcon from '@mui/icons-material/Clear';
import * as XLSX from 'xlsx';
import { getSystemVendorInvoices } from './functions/get_system_vendor_invoices.function';
import { getNonPendingSystemVendorInvoices } from './functions/get_non_pending_system_vendor_invoices.function';
import dayjs from 'dayjs';

export const SystemVendorInvoiceTable = ({ selectedTab }) => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceDateFrom, setInvoiceDateFrom] = useState(null);
  const [invoiceDateTo, setInvoiceDateTo] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [invoiceData, setInvoiceData] = useState([]);
  const [originalInvoiceData, setOriginalInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);

  const clearInvoiceDates = () => {
    setInvoiceDateFrom(null);
    setInvoiceDateTo(null);
  };

  const regions = useMemo(() => {
    const uniqueRegions = [...new Set(invoiceData.map((item) => item.region).filter(Boolean))];
    return uniqueRegions.sort();
  }, [invoiceData]);

  const statuses = useMemo(() => {
    const uniqueStatuses = [
      ...new Set(invoiceData.map((item) => item.approvalStatus).filter(Boolean)),
    ];
    return uniqueStatuses.sort();
  }, [invoiceData]);

  const handleOpenInvoiceDetails = useCallback(
    (rowData) => {
      const invoiceNumber = rowData.invoiceNumber;
      const fullInvoiceData = rowData._originalData;

      if (!fullInvoiceData) {
        const foundData = originalInvoiceData.find(
          (inv) => (inv.invoice_number || inv.invoiceNumber) === invoiceNumber
        );
        if (foundData) {
          navigate(`/systemInvoiceManagementVendor/invoiceDetails/${invoiceNumber}`, {
            state: { invoiceData: foundData },
          });
        }
        return;
      }

      navigate(`/systemInvoiceManagementVendor/invoiceDetails/${invoiceNumber}`, {
        state: { invoiceData: fullInvoiceData },
      });
    },
    [navigate, originalInvoiceData]
  );

  // eslint-disable-next-line react/prop-types
  const ActionsMenu = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleViewDetails = () => {
      handleClose();
      handleOpenInvoiceDetails(row.original);
    };

    return (
      <>
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{
            color: '#667085',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <MoreVertIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
        </Menu>
      </>
    );
  };

  // Map API response to table rows (same mapping as vendor module)
  const mapInvoiceRow = (invoice) => {
    const invoiceNumber = invoice.invoice_number || invoice.invoiceNumber || '';
    return {
      invoiceNumber,
      vendorName: invoice.vendor || invoice.vendorName || '',
      region: invoice.region || '',
      invoiceAmount: invoice.invoice_amount || invoice.invoiceAmount || '',
      approvalStatus: invoice.status || invoice.approvalStatus || '',
      createdAt: invoice.created_at || invoice.createdAt || '',
      reasonOfRejection: invoice.reason_of_rejection || invoice.reasonOfRejection || '',
      _originalData: invoice,
    };
  };

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res =
        selectedTab === 'reverted-invoices'
          ? await getNonPendingSystemVendorInvoices()
          : await getSystemVendorInvoices();

      const data = Array.isArray(res?.response)
        ? res.response
        : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];
      setOriginalInvoiceData(data);
      setInvoiceData(data.map(mapInvoiceRow));
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredData = useMemo(() => {
    let data = invoiceData;

    // Filter by Invoice Date (date-only comparison)
    // - Only start selected: show invoices for that exact date
    // - Only end selected: show invoices for that exact date
    // - Both selected: show invoices within inclusive range
    if (invoiceDateFrom || invoiceDateTo) {
      const fromStr = invoiceDateFrom ? dayjs(invoiceDateFrom).format('YYYY-MM-DD') : null;
      const toStr = invoiceDateTo ? dayjs(invoiceDateTo).format('YYYY-MM-DD') : null;

      data = data.filter((item) => {
        if (!item.createdAt) {
          return false;
        }
        const itemDateStr = dayjs(item.createdAt).format('YYYY-MM-DD');
        if (fromStr && !toStr) {
          return itemDateStr === fromStr;
        }
        if (!fromStr && toStr) {
          return itemDateStr === toStr;
        }
        return itemDateStr >= fromStr && itemDateStr <= toStr;
      });
    }

    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      data = data.filter((row) => (row.invoiceNumber || '').toLowerCase().includes(s));
    }

    if (selectedRegion) {
      data = data.filter((row) => row.region === selectedRegion);
    }

    if (selectedStatus) {
      data = data.filter((row) => row.approvalStatus === selectedStatus);
    }

    return data;
  }, [invoiceData, searchTerm, selectedRegion, selectedStatus, invoiceDateFrom, invoiceDateTo]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice Number',
        Cell: ({ cell, row }) => (
          <Button
            variant="text"
            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
            onClick={() => handleOpenInvoiceDetails(row.original)}
          >
            {cell.getValue()}
          </Button>
        ),
      },
      { accessorKey: 'vendorName', header: 'Vendor' },
      { accessorKey: 'region', header: 'Region' },
      { accessorKey: 'invoiceAmount', header: 'Amount' },
      { accessorKey: 'approvalStatus', header: 'Status' },
    ];
    if (selectedTab === 'reverted-invoices') {
      baseColumns.push({
        accessorKey: 'reasonOfRejection',
        header: 'Reason of rejection',
        size: 280,
        Cell: ({ cell }) => {
          const v = cell.getValue();
          return (
            <Box
              sx={{
                maxWidth: 360,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                color: '#344054',
              }}
            >
              {v ? String(v) : '—'}
            </Box>
          );
        },
      });
    }
    return baseColumns;
  }, [handleOpenInvoiceDetails, selectedTab]);

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    state: {
      isLoading: loading,
      pagination,
    },
    onPaginationChange: setPagination,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => <ActionsMenu row={row} />,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleOpenInvoiceDetails(row.original),
      sx: { cursor: 'pointer' },
    }),
    enablePagination: true,
    manualPagination: false,
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        {/* Left side - Search field */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            sx={{
              backgroundColor: '#fff',
              width: '300px',
              '& .MuiInputBase-input': {
                color: 'gray',
                '::placeholder': {
                  color: '#b3b9c4',
                  opacity: 1,
                },
              },
              '& .MuiOutlinedInput-root': {
                border: '1px solid #ccc',
                borderRadius: '10px',
                paddingRight: 0,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Right side - Filter dropdowns and Download button */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Invoice Start/End Date */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Invoice start date"
              value={invoiceDateFrom ? dayjs(invoiceDateFrom) : null}
              onChange={(v) => setInvoiceDateFrom(v ? v.toDate() : null)}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  placeholder: 'DD-MM-YYYY',
                  InputLabelProps: { shrink: true },
                  sx: {
                    minWidth: { xs: 140, sm: 168 },
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                  },
                },
              }}
            />
            <DatePicker
              label="Invoice end date"
              value={invoiceDateTo ? dayjs(invoiceDateTo) : null}
              onChange={(v) => setInvoiceDateTo(v ? v.toDate() : null)}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  placeholder: 'DD-MM-YYYY',
                  InputLabelProps: { shrink: true },
                  sx: {
                    minWidth: { xs: 140, sm: 168 },
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                  },
                },
              }}
            />
          </LocalizationProvider>
          {(invoiceDateFrom || invoiceDateTo) && (
            <IconButton
              size="small"
              onClick={clearInvoiceDates}
              sx={{
                padding: '4px',
                color: '#757575',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                border: '1px solid #ccc',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  color: '#333',
                  border: '1px solid #999',
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}

          {/* Region Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                REGION
              </MenuItem>
              {regions.map((region, index) => (
                <MenuItem
                  key={region}
                  value={region}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== regions.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {region}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #ccc',
                color: '#757575',
                borderRadius: '10px',
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
                '&:hover': {
                  border: '1px solid #ccc',
                },
                '&.Mui-focused': {
                  border: '1px solid #ccc',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              }}
            >
              <MenuItem
                value=""
                sx={{
                  borderBottom: '1px solid #eee',
                  fontSize: '13px',
                  color: '#656e7a',
                  lineHeight: 1.4,
                }}
              >
                STATUS
              </MenuItem>
              {statuses.map((status, index) => (
                <MenuItem
                  key={status}
                  value={status}
                  sx={{
                    fontSize: '13px',
                    color: '#656e7a',
                    lineHeight: 1.4,
                    borderBottom: index !== statuses.length - 1 ? '1px solid #eee' : 'none',
                  }}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Download button */}
          <Button
            variant="outlined"
            onClick={() => handleDownloadExcel(table)}
            disabled={filteredData.length === 0}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              minWidth: 'auto',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                border: '1px solid #ccc',
              },
              '&:disabled': {
                backgroundColor: 'transparent',
                border: '1px solid #ccc',
                color: '#999',
                opacity: 0.6,
              },
            }}
          >
            <DownloadIcon />
          </Button>
        </Box>
      </Box>
    ),
  });

  const handleDownloadExcel = (tableInstance) => {
    let dataToDownload = filteredData;
    if (tableInstance) {
      const filteredRows = tableInstance.getFilteredRowModel().rows;
      dataToDownload = filteredRows.map((row) => row.original);
    }
    if (!Array.isArray(dataToDownload) || dataToDownload.length === 0) {
      return;
    }
    const ws = XLSX.utils.json_to_sheet(
      dataToDownload.map((r) => ({ ...r, _originalData: undefined }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'invoices');
    XLSX.writeFile(wb, 'system_vendor_invoices.xlsx');
  };

  return (
    <Box sx={{ width: '100%' }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};
